import React, { useEffect, useState } from 'react';
import { Clock, Calendar, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogLayout = ({
    title,
    date,
    readTime,
    children,
    heroImage,
    description,
    listItems,
    canonicalPath,
    keywords,
    author,         // optional: { name, role, url, avatar } — overrides default Vito byline
    isoDate,        // optional: explicit ISO-8601 datePublished for JSON-LD (date prop stays for visible rendering)
}) => {
    // Default author identity for posts that don't override.
    // Existing posts keep the Vito convention; new posts can pass their own author.
    const resolvedAuthor = author || {
        name: 'Vito',
        role: 'The Orchestrator',
        url: 'https://www.roofclaw.com',
        avatar: '/vito-avatar.png',
    };
    const authorIsPerson = author && author.name && !author.isOrganization;
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        window.scrollTo(0, 0);

        const handleScroll = () => {
            const totalScroll = document.documentElement.scrollTop;
            const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scroll = `${totalScroll / windowHeight}`;
            setScrollProgress(Number(scroll));
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // JSON-LD SEO Schema
    const siteUrl = 'https://www.roofclaw.com';
    const canonicalUrl = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;
    const schemaDate = isoDate || date;
    const schemaAuthor = authorIsPerson
        ? {
            "@type": "Person",
            "name": resolvedAuthor.name,
            ...(resolvedAuthor.url ? { "url": resolvedAuthor.url } : {}),
        }
        : {
            "@type": "Organization",
            "name": "RoofClaw",
            "url": siteUrl,
        };
    const schemaData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": title,
        "description": description,
        "url": canonicalUrl,
        "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalUrl },
        "image": heroImage ? [`${siteUrl}${heroImage}`] : [],
        "datePublished": schemaDate,
        "dateModified": schemaDate,
        "inLanguage": "en-US",
        "keywords": keywords || '',
        "author": schemaAuthor,
        "publisher": {
            "@type": "Organization",
            "name": "RoofClaw",
            "url": siteUrl,
            "logo": { "@type": "ImageObject", "url": `${siteUrl}/og-image.png` }
        },
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".article-summary", "h1", "h2"]
        }
    };

    if (listItems && listItems.length > 0) {
        schemaData.mainEntity = {
            "@type": "ItemList",
            "itemListElement": listItems.map((item, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": item
            }))
        };
    }

    return (
        <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] pt-32 pb-24 relative selection:bg-[#7B61FF] selection:text-[#F0EFF4]">
            {/* SEO Schema Injection */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

            {/* Reading Progress Bar */}
            <div className="fixed top-0 left-0 w-full h-1 bg-[#18181B] z-[100]">
                <div
                    className="h-full bg-gradient-to-r from-[#7B61FF] to-[#10B981] shadow-[0_0_10px_rgba(123,97,255,0.6)] mix-blend-screen"
                    style={{ width: `${scrollProgress * 100}%` }}
                />
            </div>

            <article className="max-w-3xl mx-auto px-6">

                {/* Back Navigation */}
                <Link to="/blog" className="inline-flex items-center gap-2 text-[#F0EFF4]/60 hover:text-[#7B61FF] transition-colors mb-12 font-data text-sm uppercase tracking-wider">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Intelligence
                </Link>

                {/* Article Header */}
                <header className="mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold font-heading tracking-tight mb-8 leading-tight">
                        {title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-6 text-[#F0EFF4]/50 font-data text-sm">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-[#7B61FF]" />
                            <span>{new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-[#10B981]" />
                            <span>{readTime} MIN READ</span>
                        </div>
                    </div>

                    {/* Author Section */}
                    <div className="mt-8 flex items-center gap-4 p-4 rounded-2xl bg-[#18181B] border border-[#F0EFF4]/5 w-fit">
                        <div className="w-12 h-12 rounded-full overflow-hidden border border-[#7B61FF]/30 shadow-[0_0_10px_rgba(123,97,255,0.2)]">
                            <img src={resolvedAuthor.avatar} alt={resolvedAuthor.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                            <div className="text-[#F0EFF4] font-bold text-sm">{resolvedAuthor.name}</div>
                            <div className="text-[#F0EFF4]/40 text-xs font-data uppercase tracking-wider">{resolvedAuthor.role}</div>
                        </div>
                    </div>
                </header>

                {/* Hero Image */}
                {heroImage && (
                    <div className="mb-16 rounded-[2rem] overflow-hidden border border-[#F0EFF4]/10 shadow-[0_0_40px_rgba(123,97,255,0.1)]">
                        <img src={heroImage} alt={title} className="w-full h-auto mix-blend-screen opacity-90 hover:opacity-100 transition-opacity duration-500" />
                    </div>
                )}

                {/* Article Body Container */}
                <div className="prose prose-invert prose-lg max-w-none 
                    prose-headings:font-heading prose-headings:tracking-tight 
                    prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:font-bold prose-h2:text-[#F0EFF4]
                    prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:text-[#F0EFF4]/90
                    prose-p:text-[#F0EFF4]/70 prose-p:leading-relaxed prose-p:mb-6
                    prose-a:text-[#7B61FF] prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-[#F0EFF4] prose-strong:font-bold
                    prose-ul:text-[#F0EFF4]/70 prose-ul:my-6
                    prose-li:my-2
                    prose-blockquote:border-l-[#7B61FF] prose-blockquote:bg-[#18181B] prose-blockquote:px-6 prose-blockquote:py-4 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-[#F0EFF4]/80
                ">
                    {children}
                </div>

                {/* Footer / CTA */}
                <footer className="mt-24 pt-12 border-t border-[#F0EFF4]/10">
                    <div className="bg-[#18181B] border border-[#F0EFF4]/5 rounded-[2rem] p-8 md:p-12 text-center relative overflow-hidden group">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E')] opacity-[0.03] mix-blend-overlay"></div>
                        <h3 className="text-2xl font-bold mb-4">Secure your autonomous agent today.</h3>
                        <p className="text-[#F0EFF4]/60 mb-8 max-w-lg mx-auto">Don't risk your data with a hobbyist setup. Deploy a professional-grade, zero-trust OpenClaw agent.</p>
                        <Link to="/#reserve" className="btn-magnetic inline-flex bg-[#7B61FF] text-[#F0EFF4] px-8 py-4 rounded-full font-bold tracking-wide shadow-[0_0_20px_rgba(123,97,255,0.3)]">
                            Reserve Build
                        </Link>
                    </div>
                </footer>
            </article>
        </div>
    );
};

export default BlogLayout;
